/* SEO Articles — Batch 3 (Feb 27, 2026).
 *
 * Targets the highest-value caregiver search topics that batches 1+2 hadn't
 * fully covered: falls, incontinence, pain, hospital delirium, day-night
 * reversal, aggression de-escalation, Medicaid spend-down + look-back,
 * VA Aid & Attendance application, hospice timing, home health vs hospice,
 * bathing + dressing + meal-routine + bedroom-safety guides, caregiver
 * guilt + anger + isolation, sibling conflict, and pre-planning the
 * funeral. Same shape as ARTICLES; appended via the registry import.
 */
export const ARTICLES_BATCH_3 = [
    // ===== LEARN — clinical topics caregivers Google in crisis =====
    {
        slug: "falls-in-dementia",
        group: "learn",
        title: "Falls and Dementia — Prevention, Response, and Recovery",
        meta: "Why people with dementia fall, what to do in the moment, and the small changes that prevent the next one.",
        h1: "Falls in dementia",
        summary: "One in three older adults falls each year; people with dementia fall twice as often. Most falls are preventable — and how you respond matters as much as preventing them.",
        body: [
            { type: "h2", text: "Why dementia raises fall risk" },
            { type: "ul", items: [
                "Slower reaction time and impaired depth perception.",
                "Medications — anticholinergics, sleep aids, blood-pressure meds that drop BP on standing.",
                "Visual-spatial problems — dark floors look like holes; rugs look like obstacles.",
                "Distraction — they may try to stand and walk while talking, eating, or carrying things.",
                "Pain, UTIs, dehydration all reduce balance.",
            ]},
            { type: "h2", text: "What to do right after a fall" },
            { type: "ol", items: [
                "Don't rush to help them up. Check for pain (hip, head, wrist), blood, and confusion.",
                "If unconscious, head-strike, blood thinner use, or visible deformity — call 911.",
                "Otherwise, help them slowly to a chair. Watch for next 24 hours for new headache, vomiting, sleepiness.",
                "Document what happened: time, place, what they were doing, shoes worn, lighting.",
                "Tell the doctor — repeat falls trigger geriatric assessment + PT referral, both Medicare-covered.",
            ]},
            { type: "h2", text: "Prevent the next fall" },
            { type: "ul", items: [
                "Lighting on all walking paths, motion sensors at night.",
                "Remove throw rugs. Tape down any rug you keep.",
                "Grab bars in bathroom and any frequent transfer area.",
                "Closed shoes with non-skid soles — never socks alone on hard floors.",
                "Hearing aids + glasses worn during the day. Falls cluster around sensory gaps.",
                "Vitamin D supplement — evidence supports for fall prevention in older adults.",
                "Annual PT eval — 'Otago' fall-prevention exercises cut fall risk by ~35%.",
            ]},
            { type: "callout", tone: "coral", text: "A blood thinner + head strike = ER visit, even if they 'seem fine.' Subdural bleeds can take hours to days to show symptoms." },
        ],
        faq: [
            { q: "Is a hip protector helpful?", a: "Mixed evidence. Most don't reduce fractures in real-world wear, but they're harmless if your loved one tolerates them." },
            { q: "Should we use a wheelchair to prevent falls?", a: "Often counterproductive — losing mobility accelerates decline. Walkers + supervision usually beat permanent wheelchair use." },
        ],
        related: ["incontinence-in-dementia", "dementia-and-pain", "falls-in-dementia", "dementia-and-uti"],
        lane_prompt: "Dad fell yesterday. How do we prevent the next one?",
        updated_at: "2026-02-27",
    },
    {
        slug: "incontinence-in-dementia",
        group: "learn",
        title: "Incontinence in Dementia — Causes, Coping, and Dignity",
        meta: "Incontinence is common in middle-to-late dementia. What causes it, the schedule that prevents most accidents, and how to protect dignity.",
        h1: "Incontinence in dementia",
        summary: "Most accidents in dementia are not bladder failure — they're recognition failure. A routine, the right clothes, and the right toilet setup prevent ~70% of accidents.",
        body: [
            { type: "h2", text: "What's actually happening" },
            { type: "ul", items: [
                "They forget where the bathroom is.",
                "They no longer recognize the urge until it's too late.",
                "Clothing is confusing — buttons, zippers, belts at the wrong moment.",
                "Medications (diuretics, sleep aids) shift the timing.",
                "UTIs can cause sudden incontinence — rule out first if it's new.",
            ]},
            { type: "h2", text: "The routine that works for most families" },
            { type: "ol", items: [
                "Toilet every 2 hours — set a phone alarm.",
                "Always after meals and right before sleep.",
                "Last fluids 2 hours before bedtime; full hydration during the day.",
                "Use a commode by the bed at night if walking distance is far.",
                "Easy clothes — elastic waist pants, no buttons.",
                "Contrast the toilet seat — colored seat ring helps recognition.",
            ]},
            { type: "h2", text: "Products that help" },
            { type: "ul", items: [
                "Pull-on absorbent underwear (TENA, Depend) — easier than tabs.",
                "Mattress protectors that don't crinkle.",
                "Disposable bed pads layered for fast change-outs.",
                "Skin barrier cream (zinc oxide) at every change to prevent rashes.",
            ]},
            { type: "callout", tone: "teal", text: "Don't call them diapers. Caregivers report better acceptance with words like 'briefs,' 'special underwear,' or just no word at all." },
        ],
        faq: [
            { q: "When should we see the doctor?", a: "Always for new-onset (rule out UTI), and for nighttime-only incontinence (often a sign of sleep apnea or medication timing). Sudden change is a clinical event." },
            { q: "Do Medicare or Medicaid pay for incontinence supplies?", a: "Original Medicare: no. Many Medicaid plans: yes, with a prescription. Some Medicare Advantage plans: yes via OTC quarterly allowance." },
        ],
        related: ["falls-in-dementia", "refusing-bath", "dementia-and-uti", "wandering-prevention-checklist"],
        lane_prompt: "Mom is having accidents. How do we get on a routine?",
        updated_at: "2026-02-27",
    },
    {
        slug: "dementia-and-pain",
        group: "learn",
        title: "Pain in Dementia — The Most-Missed Symptom",
        meta: "People with dementia often can't tell you they hurt — and pain shows up as agitation, refusing care, or new behaviors. How to spot and treat it.",
        h1: "Pain in dementia",
        summary: "Up to 80% of dementia patients in late stage have pain. The most common signs are behavior changes — not 'ouch.' Treating pain often fixes the behavior.",
        body: [
            { type: "h2", text: "Signs of unrecognized pain" },
            { type: "ul", items: [
                "New agitation, irritability, or aggression.",
                "Refusing care that didn't bother them before.",
                "Wincing or guarding a body part during transfers.",
                "Resistance to sitting (back pain) or standing (knee/hip pain).",
                "Sudden disinterest in eating.",
                "Restlessness, pacing, calling out.",
                "Sleep disturbance.",
            ]},
            { type: "h2", text: "What to check" },
            { type: "ol", items: [
                "Recent fall, infection, or new diagnosis?",
                "Skin — pressure sores under clothes, especially over heels, tailbone, ears.",
                "Mouth — broken tooth, gum infection, ill-fitting denture.",
                "Constipation — easily missed, very common.",
                "UTI — confusion + new behavior in 24-72 hours.",
                "Old injuries (arthritis, prior fracture sites) that worsen with weather/cold.",
            ]},
            { type: "h2", text: "Treatment basics" },
            { type: "ul", items: [
                "Acetaminophen (Tylenol) scheduled — 650mg three times daily often makes a dramatic difference.",
                "Heat or cold packs to localized pain.",
                "Topical lidocaine patches for back/joint pain.",
                "Avoid opioids and gabapentin if possible — both worsen confusion.",
                "Anti-inflammatories (Advil, Aleve) carry GI + kidney risk in older adults — use only with doctor.",
            ]},
            { type: "callout", tone: "teal", text: "If new behaviors started in 1-2 weeks, scheduled Tylenol for 5 days is a low-risk diagnostic test. If behaviors improve, pain was the cause. If not, look elsewhere." },
        ],
        faq: [
            { q: "What pain assessment tool works for dementia?", a: "PAIN-AD scale — looks at breathing, vocalizations, facial expression, body language, consolability. Free, validated, takes 2 minutes." },
            { q: "Should we ask for hospice if pain is uncontrolled?", a: "Hospice has dedicated pain expertise — even when not yet 'dying.' A hospice palliative-care consultation alone can help." },
        ],
        related: ["agitation", "aggression", "hospice-benefit", "dementia-and-uti"],
        lane_prompt: "Could mom's new agitation be from pain we don't know about?",
        updated_at: "2026-02-27",
    },
    {
        slug: "hospital-delirium",
        group: "learn",
        title: "Hospital Delirium — Why Your Loved One Came Back 'Different'",
        meta: "Hospital stays often trigger delirium in dementia patients — confused, agitated, sometimes for weeks. Why it happens and how to prevent it.",
        h1: "Hospital delirium",
        summary: "Delirium affects up to 50% of older adults during hospital stays. It's the leading reason caregivers say their loved one 'never came back to baseline.'",
        body: [
            { type: "h2", text: "What delirium looks like vs dementia" },
            { type: "ul", items: [
                "Delirium = sudden (hours to days), fluctuates, often worse at night, attention is broken.",
                "Dementia = slow (months to years), steady, gradual decline.",
                "Delirium ON TOP of dementia = the most common cause of caregiver crisis.",
            ]},
            { type: "h2", text: "Triggers" },
            { type: "ul", items: [
                "Anesthesia or sedation.",
                "Untreated pain.",
                "Sleep disruption — hospital lights, alarms.",
                "Foley catheters, restraints, IV tethers.",
                "Sensory deprivation — no glasses, no hearing aids.",
                "New medications — benzodiazepines, opioids, anticholinergics.",
                "Infection, dehydration, electrolyte shifts.",
            ]},
            { type: "h2", text: "What to advocate for in the hospital" },
            { type: "ol", items: [
                "Hearing aids, glasses, and dentures stay in.",
                "Family at bedside as much as allowed.",
                "Day-night cues — open blinds during the day, quiet/dark at night.",
                "Walk in the hallway twice a day if cleared.",
                "Avoid restraints and Foleys when possible.",
                "Hold benzos, sleep meds, anticholinergics.",
                "Treat pain with scheduled Tylenol first.",
                "Get out of the hospital ASAP — every extra day adds risk.",
            ]},
            { type: "callout", tone: "coral", text: "Many caregivers say after discharge: 'mom never came back to herself.' Some baseline does return — but it can take 4–12 weeks. Don't accept 'this is the new dementia.' Push for re-assessment in 90 days." },
        ],
        faq: [
            { q: "Can we prevent the delirium before surgery?", a: "Yes — ask for a pre-op geriatric consult and an anesthesia plan that minimizes benzodiazepines. 'Hospital Elder Life Program (HELP)' protocols cut delirium 30-40%." },
            { q: "How fast does delirium resolve?", a: "Most resolves in days to weeks, but recovery to baseline often takes months. Some residual deficit may be permanent — talk honestly with the doctor about expected trajectory." },
        ],
        related: ["emergency-room-with-dementia", "dementia-and-uti", "dementia-and-pain", "telehealth-options"],
        lane_prompt: "Mom is so much more confused after the hospital. What's happening?",
        updated_at: "2026-02-27",
    },
    {
        slug: "caregiver-guilt",
        group: "learn",
        title: "Caregiver Guilt — Why You Feel It and How to Carry It",
        meta: "Caregiver guilt is universal in dementia care. Why it shows up, what it does to your body, and the small reframes that help.",
        h1: "Caregiver guilt",
        summary: "If you don't feel guilty, you're probably doing too little. If you only feel guilty, you're definitely doing too much. The goal isn't to eliminate it — it's to live alongside it.",
        body: [
            { type: "h2", text: "What guilt sounds like in caregivers" },
            { type: "ul", items: [
                "'I should be doing more.'",
                "'I lost my temper today.'",
                "'I put them in memory care — I broke my promise.'",
                "'I went to a movie while they sat alone.'",
                "'I want this to be over.'",
            ]},
            { type: "h2", text: "Why it's so persistent" },
            { type: "p", text: "Guilt evolved to keep humans tightly bonded to people they cared about. Dementia caregiving turns that wiring against itself — the more you love, the more guilt the brain produces, regardless of whether you've done anything wrong." },
            { type: "h2", text: "What helps" },
            { type: "ol", items: [
                "Name it out loud to someone safe. Spoken guilt fades faster than secret guilt.",
                "Substitute 'and' for 'but' — 'I love them AND I'm tired' instead of 'I love them BUT I'm tired.'",
                "Make peace with imperfect care. Dementia care has no A+ option.",
                "Watch for guilt-driven over-promising. Sentences like 'I will never put you in a home' set you up to break promises later.",
                "Therapy with a caregiver-trauma specialist — many Alzheimer's Association chapters offer sliding-scale.",
            ]},
            { type: "callout", tone: "teal", text: "Most caregivers report their guilt eased AFTER admitting their loved one to memory care — because they could finally rest enough to be present during visits. Care is not measured in hours." },
        ],
        faq: [
            { q: "Is it normal to feel relief when they die?", a: "Yes. Relief and grief coexist. Hospice grief support normalizes this — find a group within the first 6 months if you can." },
            { q: "Should I tell them I'm sorry for losing my patience?", a: "Often yes — the relationship repair matters even if they don't fully remember. Brief, calm, sincere. Then let it go." },
        ],
        related: ["caregiver-burnout-signs", "anticipatory-grief", "talking-with-siblings", "moving-to-memory-care"],
        lane_prompt: "I feel guilty all the time. Is this normal?",
        updated_at: "2026-02-27",
    },
    {
        slug: "caregiver-isolation",
        group: "learn",
        title: "Caregiver Isolation — Why It Happens and How to Rebuild",
        meta: "Dementia caregivers lose friends and community faster than almost any other group. Why it happens, and how to push back.",
        h1: "Caregiver isolation",
        summary: "Caregivers report losing 30-50% of their close relationships within 2 years of a dementia diagnosis. The losses are often invisible to outsiders.",
        body: [
            { type: "h2", text: "What's driving the loss" },
            { type: "ul", items: [
                "Friends don't know what to say so they say nothing.",
                "You can't accept invitations on short notice.",
                "Behaviors make hosting visitors stressful.",
                "Conversations are dominated by caregiving — others tire of it.",
                "Faith communities, hobby groups, family gatherings all become 'too hard.'",
            ]},
            { type: "h2", text: "What helps rebuild connection" },
            { type: "ol", items: [
                "Online dementia caregiver groups — Reddit r/dementia, Facebook groups, Alzheimer's Association message boards. Low-effort entry.",
                "Same-time-each-week phone call with one person. Predictability beats spontaneity for caregivers.",
                "Tell three trusted people SPECIFICALLY what you need. 'Bring dinner Tuesday' beats 'let me know if you need anything.'",
                "Find a caregiver-specific support group. Local Alzheimer's chapter, GUIDE program, or hospice agency.",
                "Join a structured activity with your loved one — adult day, dementia-friendly choir, memory cafe — adds connection for both of you.",
            ]},
            { type: "callout", tone: "teal", text: "The single most-protective factor against caregiver depression is one named person you can call without explaining yourself. If you don't have that today, building it is the most important next step." },
        ],
        faq: [
            { q: "What if I'm too tired to make new friends?", a: "Start with one online group — read-only at first. Your brain just needs to know other caregivers exist. Friendship can come later." },
            { q: "Is therapy worth the time?", a: "Yes, especially with a therapist who specializes in caregiver stress. CBT and ACT both have strong evidence for caregiver depression." },
        ],
        related: ["caregiver-burnout-signs", "caregiver-guilt", "anticipatory-grief", "finding-respite"],
        lane_prompt: "I feel so alone. How do I rebuild connections?",
        updated_at: "2026-02-27",
    },

    // ===== BEHAVIOR =====
    {
        slug: "aggressive-behavior-de-escalation",
        group: "behavior",
        title: "De-escalating Aggression in Dementia — 6 Steps That Work",
        meta: "When dementia aggression escalates — yelling, hitting, threatening — these are the steps that calm most situations.",
        h1: "De-escalating aggression",
        summary: "Aggression in dementia is almost always fear, pain, or overload — not anger at you. De-escalation looks like calming a scared child, not winning an argument.",
        body: [
            { type: "h2", text: "The 6-step framework" },
            { type: "ol", items: [
                "Stop. Step back two feet. Lower your voice and hands.",
                "Acknowledge: 'I can see you're upset. I'm sorry.'",
                "Don't argue, don't reason, don't correct.",
                "Reduce input: turn off TV, dim lights, ask others to leave.",
                "Offer one simple choice: 'water or juice?' or 'sit or walk?'",
                "Wait. Most episodes resolve in 5–15 minutes.",
            ]},
            { type: "h2", text: "Look for the trigger" },
            { type: "ul", items: [
                "Pain (back, head, mouth, constipation).",
                "Hunger or thirst.",
                "Need to use the bathroom.",
                "Too many people in the room.",
                "Care task started too fast (bathing, dressing).",
                "Sundowning timing.",
                "Medications — anticholinergics, benzos, opioids worsen aggression.",
            ]},
            { type: "h2", text: "If you're in physical danger" },
            { type: "ul", items: [
                "Leave the room if you can.",
                "Don't try to restrain — escalates quickly.",
                "Call 911 if there's a weapon or your safety is genuinely at risk. Ask for a CIT (Crisis Intervention Team) officer.",
                "Document time, trigger, what helped. Patterns guide treatment.",
            ]},
            { type: "callout", tone: "coral", text: "If aggression is daily and severe, the doctor may consider brexpiprazole (Rexulti), risperidone, or pimavanserin. These are real tools — not failures. Pair with environmental strategies, not as a replacement for them." },
        ],
        faq: [
            { q: "Should we restrain them?", a: "Almost never. Restraints (physical or chemical) cause more injuries and worsen behavior. Reserve for true emergency." },
            { q: "Will hitting and yelling continue forever?", a: "Often peaks in middle stage and decreases in late stage as mobility and language fade. Most families remember middle stage as the hardest." },
        ],
        related: ["aggression", "agitation", "catastrophic-reactions", "brexpiprazole-rexulti", "dementia-and-pain"],
        lane_prompt: "Dad got physical with me today. How do I de-escalate next time?",
        updated_at: "2026-02-27",
    },
    {
        slug: "day-night-reversal",
        group: "behavior",
        title: "Day-Night Reversal — When They Sleep All Day and Roam All Night",
        meta: "Dementia can flip the sleep schedule entirely. Why it happens and how to nudge it back.",
        h1: "Day-night reversal",
        summary: "Day-night reversal is exhausting — and one of the most treatable behaviors when caught early.",
        body: [
            { type: "h2", text: "Why it flips" },
            { type: "ul", items: [
                "Circadian rhythm depends on light cues — dementia patients often spend days indoors with dim light.",
                "Sleeping during the day removes the pressure to sleep at night.",
                "Sundowning agitation makes evening sleep harder.",
                "Caffeine and nighttime fluids contribute.",
                "Medications: stimulants, steroids, late diuretics, antidepressants.",
            ]},
            { type: "h2", text: "Reset the rhythm" },
            { type: "ol", items: [
                "Bright light exposure first thing in morning — outside if possible, or by a sunny window. 30 minutes.",
                "Block daytime naps after 2 PM. Short rest fine; long naps not.",
                "Walk or move every afternoon — fatigue helps sleep.",
                "No caffeine after noon.",
                "Last meal 3 hours before bedtime.",
                "Bedroom cool, dark, quiet. Blackout curtains.",
                "Predictable evening routine — same order every night.",
            ]},
            { type: "h2", text: "Safety overnight if reversal persists" },
            { type: "ul", items: [
                "Door alarms on bedroom + bathroom.",
                "Motion-sensor nightlights along the path.",
                "Bed alarm pad — alerts you when they get up.",
                "Lock kitchen + stove if they wander to cook.",
                "Sleep meds: melatonin (3–5mg) is safest first try. Avoid benzos and sleep aids if at all possible.",
            ]},
            { type: "callout", tone: "teal", text: "If reversal lasts more than 2–3 weeks despite environmental changes, talk to the doctor about a sleep evaluation — sleep apnea is common in dementia and often undiagnosed." },
        ],
        faq: [
            { q: "Is trazodone safe?", a: "Often used in dementia, lower-risk than benzos or Z-drugs. Discuss with the doctor — usually 25–50mg at bedtime." },
            { q: "Will adult day help?", a: "Yes — significantly. Structured daytime stimulation often resets the schedule within 2–3 weeks." },
        ],
        related: ["sleep-disturbance", "sundowning", "wandering", "shadowing"],
        lane_prompt: "Dad sleeps all day and roams all night. How do we reset?",
        updated_at: "2026-02-27",
    },
    {
        slug: "wandering-prevention-checklist",
        group: "behavior",
        title: "Wandering Prevention Checklist — Room by Room",
        meta: "A practical home walkthrough to prevent dementia wandering. What to add, what to remove, what to install.",
        h1: "Wandering prevention checklist",
        summary: "Most wandering happens through a familiar door at a routine time. A handful of inexpensive changes prevent ~80% of incidents.",
        body: [
            { type: "h2", text: "Doors and exits" },
            { type: "ul", items: [
                "Door alarms (~$15) on every external door + garage door.",
                "Slide bolts mounted ABOVE eye level on external doors.",
                "Doorknob covers if turning is still possible.",
                "Cover or camouflage the door — a curtain, mural, or 'STOP' sign reduces interest by ~50%.",
                "Black mat in front of door — many dementia patients perceive it as a hole and avoid stepping over.",
            ]},
            { type: "h2", text: "Windows + access points" },
            { type: "ul", items: [
                "Window locks installed and tested.",
                "Sliding doors with security bars.",
                "Garage door opener kept inaccessible — keys, fobs, remotes.",
            ]},
            { type: "h2", text: "Identification + tracking" },
            { type: "ul", items: [
                "MedicAlert + Safe Return bracelet (Alzheimer's Association — ~$50/year).",
                "GPS tracker — AirTag in pocket, Apple Watch with cellular, or dedicated tracker like Jiobit or AngelSense.",
                "Photo of your loved one + key info on the fridge for first responders.",
                "Local Project Lifesaver enrollment — wristband tracker monitored by police/fire.",
            ]},
            { type: "h2", text: "Routines that reduce wandering urge" },
            { type: "ul", items: [
                "Daily outdoor walk — burns the 'I need to go somewhere' impulse.",
                "Predictable schedule — meals, activities, rest at the same times.",
                "Adult day program — most effective single intervention for wandering.",
                "Identify your loved one's 'wandering window' (often late afternoon) and plan engaging activity then.",
            ]},
            { type: "callout", tone: "coral", text: "If your loved one wanders away, call 911 immediately. Most wanderers are found within 1 mile of home, often near water. Don't wait to 'see if they come back.'" },
        ],
        faq: [
            { q: "Do GPS trackers work?", a: "Yes, especially watch-based ones that stay on. Pocket trackers fail when clothing changes. Test the tracker monthly." },
            { q: "When is wandering a sign for memory care?", a: "When containment is creating unsafe conditions (locked doors becoming fire hazards) or when episodes happen multiple times despite good prevention." },
        ],
        related: ["wandering", "exit-seeking", "moving-to-memory-care", "wandering-prevention-checklist"],
        lane_prompt: "How do I dementia-proof our home against wandering?",
        updated_at: "2026-02-27",
    },

    // ===== PAYING FOR CARE =====
    {
        slug: "medicaid-asset-spend-down",
        group: "paying-for-care",
        title: "Medicaid Asset Spend-Down — How It Actually Works",
        meta: "Medicaid pays for nursing home care once savings are drawn down. What counts as an asset, what's exempt, and how to spend down legally.",
        h1: "Medicaid asset spend-down",
        summary: "Medicaid is the largest payer of long-term care in the US. Qualifying requires reducing 'countable' assets to roughly $2,000–$3,000 (single) or $148,620 (community spouse, 2026). Done right, your family keeps more than you think.",
        body: [
            { type: "h2", text: "Countable vs exempt assets" },
            { type: "ul", items: [
                "Countable: bank accounts, stocks, second homes, life insurance with cash value over $1,500, retirement accounts (most states).",
                "Exempt while spouse is alive: primary home (up to $730,000 equity, varies by state), one car, household furnishings, irrevocable burial trust, term life insurance, personal effects.",
                "Community spouse resource allowance (CSRA): the well spouse keeps up to $148,620 in 2026. Maximum varies; minimum is $30,828.",
            ]},
            { type: "h2", text: "Legal spend-down strategies" },
            { type: "ol", items: [
                "Pay off debt — mortgage, credit cards, car loans.",
                "Home modifications — ramp, walk-in shower, stair lift, accessibility.",
                "Replace old vehicles (one allowed, no value cap).",
                "Pre-pay funeral costs into an irrevocable burial trust.",
                "Medicaid Compliant Annuity for the well spouse — converts countable asset to non-countable income stream.",
                "Pay caregiving family members through a properly-structured Personal Care Agreement.",
                "Spousal refusal (in some states like NY, FL) — community spouse refuses to contribute.",
            ]},
            { type: "callout", tone: "coral", text: "DO NOT just 'give the money to the kids.' Gifts during the 5-year look-back trigger Medicaid penalty periods that can leave your loved one without coverage when they need it most." },
            { type: "h2", text: "Document everything" },
            { type: "ul", items: [
                "Every check, every receipt, every asset transfer.",
                "Keep records for 5+ years.",
                "Medicaid applications routinely audit. Sloppy paperwork costs months of coverage.",
            ]},
        ],
        faq: [
            { q: "Can we just transfer the house?", a: "Sometimes — to a spouse, a disabled child, or a 'caretaker child' who lived in the home for 2+ years providing care. Otherwise, a transfer within 5 years creates a penalty. Talk to an elder-law attorney." },
            { q: "Do we need an attorney?", a: "For any estate over $200K — yes. Attorney fees ($3,000-$8,000) are tiny compared to the assets a single mistake can disqualify." },
        ],
        related: ["medicaid-waivers", "medicaid-look-back-period", "selling-the-house-to-pay", "power-of-attorney"],
        lane_prompt: "How do we spend down to qualify for Medicaid?",
        updated_at: "2026-02-27",
    },
    {
        slug: "medicaid-look-back-period",
        group: "paying-for-care",
        title: "The Medicaid 5-Year Look-Back — What It Penalizes",
        meta: "Medicaid examines 5 years of financial history before approving nursing-home coverage. What triggers penalties and how to plan early.",
        h1: "Medicaid 5-year look-back",
        summary: "Every Medicaid nursing-home application reviews 5 years of bank statements. Asset transfers under fair-market value during that window create a penalty period.",
        body: [
            { type: "h2", text: "What 'penalty period' means" },
            { type: "p", text: "If you gave away $100,000 within the 5-year window, Medicaid calculates a penalty: that gift divided by your state's average monthly nursing home cost (~$11,000). $100K / $11K = ~9 months. During those 9 months, Medicaid won't pay — even though you're financially destitute. The family pays cash or the patient leaves the facility." },
            { type: "h2", text: "What counts as a transfer" },
            { type: "ul", items: [
                "Gifts to family — even $200 birthday checks add up over 5 years.",
                "Selling the house below market value to a relative.",
                "Adding a child's name to a deed.",
                "Forgiving a loan.",
                "Charitable donations.",
            ]},
            { type: "h2", text: "What does NOT count" },
            { type: "ul", items: [
                "Transfers to a spouse.",
                "Transfers to a disabled child of any age.",
                "Transfer of the home to a 'caretaker child' who lived in and cared for the parent 2+ years.",
                "Transfers to a special needs trust for a disabled person under 65.",
                "Spending on legitimate care, home modifications, pre-paid funerals.",
            ]},
            { type: "h2", text: "Start planning 5+ years before need" },
            { type: "ul", items: [
                "Establish a power of attorney now.",
                "If you're considering an Irrevocable Asset Protection Trust, the 5-year clock starts the day funds are transferred — so plan EARLY.",
                "Even if a diagnosis already exists, partial planning is often possible. Consult an elder-law attorney.",
            ]},
            { type: "callout", tone: "teal", text: "California is the only state without a look-back as of 2026 (and even there, federal rules may change). Don't assume your state's rules without checking with an attorney." },
        ],
        faq: [
            { q: "Does the look-back apply to in-home Medicaid?", a: "No — only to nursing home Medicaid in most states. HCBS waivers usually have shorter or no look-back, but rules vary by state." },
            { q: "What if we genuinely needed to spend the money?", a: "Document why. Medicaid case workers can excuse transfers for legitimate purposes (medical bills, home repairs, taxes) with receipts." },
        ],
        related: ["medicaid-asset-spend-down", "medicaid-waivers", "power-of-attorney", "legal-documents-checklist"],
        lane_prompt: "What is the Medicaid look-back period and how do we avoid penalties?",
        updated_at: "2026-02-27",
    },
    {
        slug: "va-aid-and-attendance-applying",
        group: "paying-for-care",
        title: "VA Aid & Attendance — How to Actually Apply",
        meta: "Veterans and surviving spouses can receive up to $2,895/month tax-free for caregiving. Step-by-step application guide.",
        h1: "Applying for VA Aid & Attendance",
        summary: "Aid & Attendance is the VA's most underutilized benefit — only ~1/3 of eligible families apply. Approval typically takes 3–6 months and is retroactive to application date.",
        body: [
            { type: "h2", text: "Who qualifies (2026 rules)" },
            { type: "ul", items: [
                "Wartime veteran (any branch, 90+ days active duty with at least 1 day during wartime).",
                "Honorable discharge.",
                "Needs help with at least 2 activities of daily living OR is bedridden OR has severe vision loss OR is in assisted living/memory care.",
                "Net worth under ~$159,240 (2026 cap, excludes home + 1 car).",
                "Income minus medical expenses under benefit threshold.",
            ]},
            { type: "h2", text: "2026 maximum monthly benefits" },
            { type: "ul", items: [
                "Single veteran: $2,895/month tax-free.",
                "Married veteran: $3,432/month tax-free.",
                "Surviving spouse: $1,859/month tax-free.",
                "Both veteran and spouse alive, both eligible: $4,594/month tax-free.",
            ]},
            { type: "h2", text: "Application steps" },
            { type: "ol", items: [
                "Get form 21-2680 (Aid & Attendance Examination) completed by the veteran's doctor.",
                "Get form 21P-527EZ (Pension Application — veterans) OR 21P-534EZ (surviving spouses).",
                "Gather: DD-214 discharge, marriage certificates, death certificate if surviving spouse, bank statements, medical bills, list of unreimbursed medical expenses.",
                "Submit through a VA-accredited claims agent or VSO (Veterans Service Organization) — DAV, VFW, AmVets. Free.",
                "Or apply directly at va.gov/pension.",
            ]},
            { type: "h2", text: "Common pitfalls" },
            { type: "ul", items: [
                "Working with non-accredited 'pension poachers' who sell annuities — illegal in many states; always use a VSO or accredited attorney.",
                "Not deducting unreimbursed medical expenses from income — this is what gets most families approved.",
                "Missing the DD-214. Order from the National Archives if lost.",
            ]},
            { type: "callout", tone: "teal", text: "Don't self-screen out. Many families think they earn 'too much' but assisted living, in-home aides, and medical bills can reduce countable income dramatically. Free VSO help is always worth the call." },
        ],
        faq: [
            { q: "Can we get Aid & Attendance + Medicaid at the same time?", a: "Yes, but the structure matters. The benefit counts as income for Medicaid eligibility. An attorney can help avoid double-counting issues." },
            { q: "How long does it take?", a: "Typically 3-6 months. Approval is retroactive to the month after application, so file early." },
        ],
        related: ["va-aid-and-attendance", "medicaid-asset-spend-down", "ltc-insurance", "social-security-disability-dementia"],
        lane_prompt: "Dad's a veteran. How do we apply for Aid & Attendance?",
        updated_at: "2026-02-27",
    },

    // ===== GUIDE / PROGRAMS =====
    {
        slug: "hospice-when-to-call",
        group: "guide",
        title: "When to Call Hospice — The Honest Signs",
        meta: "Most families wait too long to call hospice. Here are the actual signs — and why earlier hospice usually improves outcomes.",
        h1: "When to call hospice",
        summary: "Hospice is for the last 6 months of life. Most dementia families call in the last 6 days — and regret it. Earlier is better.",
        body: [
            { type: "h2", text: "Specific signs to call now" },
            { type: "ul", items: [
                "Significant weight loss (10%+ in 6 months) or stopped eating.",
                "Multiple hospitalizations in 6 months.",
                "Recurrent UTIs or pneumonia.",
                "Stopped walking, stopped meaningful conversation.",
                "Loss of recognition of close family.",
                "Pressure injuries or skin breakdown.",
                "FAST stage 7c or beyond.",
                "Caregiver is burning out.",
            ]},
            { type: "h2", text: "What hospice actually does" },
            { type: "ul", items: [
                "Nurse visits 1-3x/week.",
                "Aide for bathing 2-5x/week.",
                "Social worker + chaplain on call.",
                "All comfort medications delivered.",
                "Hospital bed, oxygen, wheelchair as needed.",
                "24/7 nurse phone line.",
                "5-day inpatient respite if you need a break.",
                "13 months of grief support after death.",
                "Covered 100% by Medicare — no copay.",
            ]},
            { type: "h2", text: "Hospice myths" },
            { type: "ul", items: [
                "'Hospice means giving up' — no. Studies show hospice patients often LIVE LONGER than non-hospice patients at end of life.",
                "'Hospice is only for cancer' — no. Dementia is now one of the largest hospice diagnoses.",
                "'Hospice doesn't treat infections' — they do, when the goal is comfort.",
                "'Once on hospice, you can't leave' — false. You can revoke any time and resume regular Medicare.",
            ]},
            { type: "callout", tone: "teal", text: "Ask any local hospice for a 'free informational visit.' No commitment, no admission. Most families wish they'd had the conversation 6 months earlier." },
        ],
        faq: [
            { q: "Can hospice come to memory care?", a: "Yes. Hospice goes wherever the person lives — home, assisted living, memory care, nursing home." },
            { q: "What if they live longer than 6 months?", a: "Hospice continues — they're recertified at each benefit period. Many dementia patients are on hospice 12+ months." },
        ],
        related: ["hospice-benefit", "home-health-vs-hospice", "when-to-stop-treatment", "end-of-life-wishes"],
        lane_prompt: "Is it time to call hospice for mom?",
        updated_at: "2026-02-27",
    },
    {
        slug: "home-health-vs-hospice",
        group: "guide",
        title: "Home Health vs Hospice — What's the Difference?",
        meta: "Home health and hospice both come to the house, both covered by Medicare. They serve different stages and different goals.",
        h1: "Home health vs hospice",
        summary: "Home health is short-term skilled care during recovery. Hospice is comfort care at end of life. Many families need both — at different times.",
        body: [
            { type: "h2", text: "Quick comparison" },
            { type: "ul", items: [
                "Home Health = skilled nursing, PT/OT, wound care. Typically after hospital stay, 1-8 weeks. Covered if 'homebound' + physician order.",
                "Hospice = comfort-focused, end-of-life. 6-month prognosis. Covered for as long as needed.",
                "Both: Medicare-covered with no copay. Both come to wherever the patient lives.",
            ]},
            { type: "h2", text: "When to use Home Health" },
            { type: "ul", items: [
                "After a hospital stay — recovery, PT, wound care.",
                "New medication or device that needs nursing oversight.",
                "After a fall — PT to prevent the next one.",
                "Caregiver training on new equipment.",
            ]},
            { type: "h2", text: "When to use Hospice" },
            { type: "ul", items: [
                "Goal has shifted from treating disease to maximizing comfort.",
                "6 months or less life expectancy (rough estimate; can extend).",
                "Recurrent hospitalizations the family no longer wants.",
                "Caregiver needs more support than home health provides.",
            ]},
            { type: "h2", text: "What you don't get with home health" },
            { type: "ul", items: [
                "Long-term help with bathing, dressing, meal prep (use a paid aide or Medicaid HCBS).",
                "Overnight care.",
                "Companion services.",
                "24/7 nurse phone line.",
            ]},
            { type: "callout", tone: "teal", text: "Many families bounce between home health and hospice as needs change. A good case manager (often through GUIDE) helps coordinate the transitions." },
        ],
        faq: [
            { q: "Can we have both at the same time?", a: "Generally no — Medicare requires you to choose. Home health stops when hospice starts for the same condition." },
            { q: "What's palliative care vs hospice?", a: "Palliative care is comfort-focused care at any stage, often alongside curative treatment. Hospice is palliative care at end of life. Palliative is good earlier; hospice is the next chapter." },
        ],
        related: ["hospice-benefit", "hospice-when-to-call", "telehealth-options", "guide-eligibility"],
        lane_prompt: "What's the difference between home health and hospice?",
        updated_at: "2026-02-27",
    },

    // ===== DAILY CARE =====
    {
        slug: "bathing-tips-dementia",
        group: "resources",
        title: "Bathing a Loved One With Dementia — A Practical Guide",
        meta: "Bathing is one of the hardest dementia care tasks. Why it's so resisted, and the small adjustments that change everything.",
        h1: "Bathing in dementia",
        summary: "Most bathing struggles aren't about hygiene — they're about cold, fear, modesty, and loss of control. The right setup turns most fights into a 10-minute ritual.",
        body: [
            { type: "h2", text: "Why bathing is hard" },
            { type: "ul", items: [
                "Cold air feels colder when you're vulnerable.",
                "Water on the face triggers fear (drowning, dunking).",
                "Bright bathroom lights are harsh and unfamiliar.",
                "Loss of modesty is sharp at moments of being uncovered.",
                "Showers feel chaotic — sound, water pressure, slippery surfaces all at once.",
            ]},
            { type: "h2", text: "Set up for success" },
            { type: "ul", items: [
                "Warm room — heat lamp or space heater, 75–80°F.",
                "Towels warmed in the dryer.",
                "Soft, dim lighting — bathroom too clinical otherwise.",
                "Familiar music playing.",
                "Bath bench, grab bars, hand-held shower head.",
                "Test water on YOUR wrist first.",
                "Two towels — one to cover, one to dry.",
            ]},
            { type: "h2", text: "Approach the task" },
            { type: "ol", items: [
                "Avoid asking 'do you want a shower?' — they'll say no. Instead, lead: 'Let's freshen up before lunch.'",
                "Bathe at the time of day they're most calm (often mid-morning).",
                "Don't bathe daily — 2-3x per week is fine. Sponge baths between work.",
                "Keep them covered with a towel during the entire process, sliding water under.",
                "Talk through what you're doing: 'I'm going to wash your arm now.'",
                "Save hair-washing for last or skip — many caregivers do dry shampoo.",
                "If they refuse, stop. Try again in 30 minutes or the next day.",
            ]},
            { type: "callout", tone: "teal", text: "Some families adopt a 'sponge bath only' practice for late-stage dementia. It's gentler, faster, and adequate. Don't fight battles you don't need to fight." },
        ],
        faq: [
            { q: "What if they hit when we try to bathe them?", a: "Stop, step back, try later. Aggression during bathing is fear — not resistance. Treat the fear, not the behavior." },
            { q: "Is there a no-rinse bathing option?", a: "Yes — no-rinse body wash, Comfort Bath wipes, dry shampoo. These work surprisingly well and reduce daily distress." },
        ],
        related: ["refusing-bath", "refusing-care", "aggressive-behavior-de-escalation", "dementia-and-pain"],
        lane_prompt: "Mom refuses to shower. How do we approach this?",
        updated_at: "2026-02-27",
    },
    {
        slug: "dressing-dementia",
        group: "resources",
        title: "Dressing a Loved One With Dementia — Calmer Mornings",
        meta: "Dressing is a daily struggle. Easier clothes, smarter routines, and dignity-first approaches that cut conflict by half.",
        h1: "Dressing in dementia",
        summary: "Most dressing struggles come from too many choices, complicated fasteners, or rushing. Simplify everything and you cut conflict in half.",
        body: [
            { type: "h2", text: "Set up the closet" },
            { type: "ul", items: [
                "Reduce options — 3-5 outfits total. Rotate seasonally.",
                "Same outfit every day if they love it. No one cares.",
                "Pull-on pants — elastic waist, no zippers or buttons.",
                "Velcro shoes or slip-ons.",
                "Front-closure bras or sports bras.",
                "Magnetic-button shirts (Joe & Bella, MagnaReady).",
                "Cardigans instead of pullover sweaters.",
            ]},
            { type: "h2", text: "Routine that works" },
            { type: "ol", items: [
                "Lay clothes out in the order they go on.",
                "Hand them ONE item at a time. Multiple items overwhelm.",
                "Stand them next to a chair to lean on.",
                "Slow down. Allow extra time. Rushing creates resistance.",
                "Praise each step — 'good, now the other arm.'",
                "Save tricky things (shoes, buttons) for end when they're standing.",
            ]},
            { type: "h2", text: "If they refuse" },
            { type: "ul", items: [
                "Walk away. Try in 15 minutes.",
                "Offer a choice between two acceptable options ('blue or green?').",
                "Layer for warmth without daily change — pajamas under clothes is fine.",
                "Some days they wear yesterday's clothes. That's okay.",
            ]},
            { type: "callout", tone: "teal", text: "Adaptive clothing has come a long way. Brands like Joe & Bella, MagnaReady, Silvert's, Buck & Buck are worth every dollar — caregivers say they're the single best money spent on daily care." },
        ],
        faq: [
            { q: "Should we let them dress themselves?", a: "Yes, as much as possible. Layout helps. Hand-over-hand assistance preserves dignity and skill." },
            { q: "What about overnight outfits?", a: "Soft pajamas that double as loungewear. Many caregivers report fewer 'changing' battles when day and night clothes look similar." },
        ],
        related: ["bathing-tips-dementia", "refusing-care", "mealtime-routines-dementia", "incontinence-in-dementia"],
        lane_prompt: "How do I make morning dressing less of a battle?",
        updated_at: "2026-02-27",
    },
    {
        slug: "mealtime-routines-dementia",
        group: "resources",
        title: "Mealtime Routines for Dementia — Calm, Safe, Nourishing",
        meta: "How to set up dementia mealtimes for maximum success — environment, food, plate setup, social cues.",
        h1: "Mealtime routines",
        summary: "Mealtimes are about more than nutrition — they're sensory, social, and ritual. Get the routine right and most other care gets easier too.",
        body: [
            { type: "h2", text: "The environment" },
            { type: "ul", items: [
                "Same place, same time. Predictability soothes.",
                "TV off. Phone away. One sound at a time.",
                "Good lighting. Older eyes need 2-3x more light than yours.",
                "Contrasting plate — red plate, white food. Easier to see.",
                "One dish at a time. Crowded table overwhelms.",
            ]},
            { type: "h2", text: "The food" },
            { type: "ul", items: [
                "Finger foods if utensils confuse — sandwiches, fruit, meatballs.",
                "Soft foods if chewing is hard — scrambled eggs, oatmeal, mashed potatoes.",
                "Sweet flavors stay last — keep dessert when other foods are refused.",
                "Smoothies, milkshakes, peanut-butter shakes if intake is low.",
                "Avoid mixed textures (soup with chunks) if swallowing is changing.",
            ]},
            { type: "h2", text: "The cues" },
            { type: "ul", items: [
                "Eat with them. Mirroring triggers the action.",
                "Hand-over-hand: place utensil in hand and guide the first bite.",
                "Verbal prompts: 'try this' or 'one more bite.'",
                "Allow extra time — 30-45 minutes per meal is normal.",
                "Don't insist on cleaning the plate — small frequent meals beat large rigid ones.",
            ]},
            { type: "h2", text: "Safety" },
            { type: "ul", items: [
                "Watch for coughing during drinks — early sign of dysphagia.",
                "Thicken liquids if recommended by speech therapist.",
                "Sit upright for 30 minutes after eating to prevent aspiration.",
                "Check oral health monthly — broken teeth and gum infections are commonly missed causes of refusal.",
            ]},
            { type: "callout", tone: "coral", text: "Late-stage refusal to eat may signal the body is starting to shut down. Talk to the doctor about hospice — not a feeding tube. American Geriatrics Society explicitly recommends against feeding tubes in advanced dementia." },
        ],
        faq: [
            { q: "Should we add Ensure or Boost?", a: "If intake is low, yes. Many dementia patients prefer the texture and sweetness of supplement shakes. Ice cream and milkshakes work just as well." },
            { q: "What about choking?", a: "Sit upright, small bites, watch for wet-sounding voice or coughing. If concerned, request a speech-language pathologist evaluation (Medicare-covered)." },
        ],
        related: ["dementia-and-eating-difficulties", "bathing-tips-dementia", "dressing-dementia", "hospice-when-to-call"],
        lane_prompt: "How do we set up mealtimes for someone with dementia?",
        updated_at: "2026-02-27",
    },
    {
        slug: "dementia-friendly-bedroom",
        group: "resources",
        title: "Setting Up a Dementia-Friendly Bedroom",
        meta: "Sleep is sacred for caregivers. A dementia-friendly bedroom prevents nighttime falls, wandering, and sundowning chaos.",
        h1: "Dementia-friendly bedroom",
        summary: "Most nighttime crises happen in the bedroom or on the way to the bathroom. A few small changes prevent the majority of them.",
        body: [
            { type: "h2", text: "Layout" },
            { type: "ul", items: [
                "Bed against a wall on at least one side to prevent rolling out.",
                "Clear path to the bathroom — no rugs, cords, or low furniture.",
                "Floor-level bed if falls are a risk (or a low-profile bed frame).",
                "Pad floor next to bed for soft landings.",
            ]},
            { type: "h2", text: "Lighting" },
            { type: "ul", items: [
                "Motion-sensor nightlights along the path to the bathroom.",
                "Lamp by the bed with an easy switch (or smart bulb with voice control).",
                "Blackout curtains for sleep, but a light source they can find for bathroom trips.",
                "Avoid harsh overhead lights at night — disorients the brain.",
            ]},
            { type: "h2", text: "Safety equipment" },
            { type: "ul", items: [
                "Bed alarm pad — alerts you when they get up.",
                "Door alarm on bedroom door if wandering is a risk.",
                "Baby monitor (audio or video) on your nightstand.",
                "Grab bar by the bed for safe transfers.",
                "Commode by the bed if walking distance is long.",
            ]},
            { type: "h2", text: "Comfort" },
            { type: "ul", items: [
                "Weighted blanket (10-15 lbs) — many caregivers report dramatic calming effect.",
                "Soft bedding in a familiar pattern.",
                "Familiar object on the nightstand — photo, watch, faith item.",
                "White noise machine or fan — masks ambient sounds.",
                "Cool temperature (65-68°F) supports sleep.",
            ]},
            { type: "callout", tone: "teal", text: "A weighted blanket is one of the simplest, cheapest interventions for sundowning + sleep. Many caregivers wish they'd tried it sooner." },
        ],
        faq: [
            { q: "Are bed rails safe?", a: "Generally no — increasing risk of entrapment. Floor-level beds with floor pads are safer." },
            { q: "Should we share a bed?", a: "Depends. Many caregivers move to twin beds or separate rooms for their own sleep. Both are valid choices — what protects YOUR sleep protects the caregiving." },
        ],
        related: ["sleep-disturbance", "wandering-prevention-checklist", "falls-in-dementia", "hospital-delirium"],
        lane_prompt: "How do we set up a safe bedroom for someone with dementia?",
        updated_at: "2026-02-27",
    },

    // ===== HARD CONVERSATIONS =====
    {
        slug: "sibling-conflict-over-care",
        group: "hard-conversations",
        title: "When Siblings Fight Over Mom's Care",
        meta: "Sibling conflict over dementia care is the norm, not the exception. Why it happens and how to navigate without burning the family down.",
        h1: "Sibling conflict over care",
        summary: "Dementia surfaces every old family wound. The fights are rarely about care — they're about love, grief, control, and money. Name that and the conversations get easier.",
        body: [
            { type: "h2", text: "What's actually happening" },
            { type: "ul", items: [
                "Different siblings hold different memories of mom. They're not wrong — just incomplete.",
                "Anticipatory grief shows up as anger or absence.",
                "Money fears get projected onto care decisions.",
                "Geographic distance creates unequal labor — and resentment.",
                "Birth order, gender roles, and history play out in real time.",
            ]},
            { type: "h2", text: "What helps" },
            { type: "ol", items: [
                "Designate ONE primary decision-maker (the named POA). Others advise; one decides.",
                "Family meetings at scheduled times, not crisis-driven.",
                "Use a neutral facilitator — social worker, geriatric care manager, family therapist.",
                "Share information in writing — same email to all siblings, not phone calls that turn into 'he said she said.'",
                "Pay distant siblings to come help — buying them out of guilt is cheaper than fighting.",
                "Compensate the in-town caregiver fairly. A Personal Care Agreement is a real legal document.",
            ]},
            { type: "h2", text: "When it gets ugly" },
            { type: "ul", items: [
                "Set boundaries: 'I'm happy to discuss care, not your opinion of me.'",
                "Limit communication to the necessary minimum.",
                "Document everything — finances, medical decisions, time spent.",
                "Get your own therapist. The family wound is real and yours alone to heal.",
            ]},
            { type: "callout", tone: "coral", text: "If a sibling threatens lawsuits, files Adult Protective Services complaints, or accuses you of theft — get an attorney TODAY. These conflicts escalate fast and the burden of proof falls on the accused." },
        ],
        faq: [
            { q: "What if my sibling won't help at all?", a: "You can't force them. Focus on what you control — your boundaries, your support system, your documentation. Many families come back together AFTER the parent dies." },
            { q: "Should we hire a geriatric care manager?", a: "Often yes — neutral expert advice de-escalates fights. ~$150-200/hour but saves thousands in family damage." },
        ],
        related: ["talking-with-siblings", "caregiver-guilt", "caregiver-burnout-signs", "power-of-attorney"],
        lane_prompt: "My siblings and I are fighting over mom's care. How do we move forward?",
        updated_at: "2026-02-27",
    },
    {
        slug: "planning-the-funeral-early",
        group: "hard-conversations",
        title: "Planning the Funeral While They're Still Here",
        meta: "Pre-planning a funeral feels morbid — and it's one of the most loving things a family can do. How to start the conversation.",
        h1: "Pre-planning the funeral",
        summary: "Pre-planning lets you make calm decisions instead of grief-stricken ones. It also avoids family fights and protects assets for Medicaid spend-down.",
        body: [
            { type: "h2", text: "Why now" },
            { type: "ul", items: [
                "Decisions made in grief cost 2-3x more.",
                "Family fights over funeral choices are bitter and lasting.",
                "Pre-paid funeral trusts are exempt from Medicaid spend-down.",
                "Including your loved one in their own plan (when possible) is dignifying.",
            ]},
            { type: "h2", text: "What to decide" },
            { type: "ul", items: [
                "Burial or cremation.",
                "Religious or secular service.",
                "Open vs closed casket.",
                "Where to be buried or scattered.",
                "Who speaks. What music.",
                "Obituary text — write the first draft while they can review it.",
                "Charitable donations 'in lieu of flowers.'",
            ]},
            { type: "h2", text: "Financial planning" },
            { type: "ol", items: [
                "Get 3 funeral home quotes. Prices vary 2-3x for the same service.",
                "Pre-pay into an irrevocable burial trust — Medicaid-exempt up to ~$15,000.",
                "Pre-arrange but don't pre-pay if cash flow is tight — pricing is locked at today's rates with most funeral homes.",
                "Veterans Burial Benefit — VA covers burial in a national cemetery + headstone + flag.",
                "Many faith communities have funeral funds — ask.",
            ]},
            { type: "h2", text: "How to start the conversation" },
            { type: "ul", items: [
                "Frame it as a gift to YOU: 'I want to know I'm honoring you the way you want.'",
                "Pick a calm moment, not after a hospitalization.",
                "Use a tool — Five Wishes, COMPASSION & SUPPORT, or a hospice intake form to prompt.",
                "Talk in pieces over weeks, not one big conversation.",
            ]},
            { type: "callout", tone: "teal", text: "Most caregivers say the funeral planning conversation, once started, brought them closer than any other. Don't avoid the topic out of fear — your loved one likely WANTS to be part of the decision." },
        ],
        faq: [
            { q: "What if they refuse to discuss it?", a: "Don't force. Talk to siblings instead. Plan as much as you can without them — they may join later when ready." },
            { q: "What about a green burial?", a: "Increasingly available — no embalming, biodegradable casket or shroud, no vault. Less expensive and gentler on the earth. Many states now allow." },
        ],
        related: ["end-of-life-wishes", "when-to-stop-treatment", "hospice-when-to-call", "medicaid-asset-spend-down"],
        lane_prompt: "How do we plan the funeral while mom is still here?",
        updated_at: "2026-02-27",
    },

    // ===== LEARN — three more high-value =====
    {
        slug: "dementia-medications-overview",
        group: "learn",
        title: "Dementia Medications — What's Available in 2026",
        meta: "Aricept, Namenda, Leqembi, Kisunla. A plain-English guide to dementia medications, what they do, and what to expect.",
        h1: "Dementia medications",
        summary: "No medication cures dementia. Several slow decline modestly, two slow disease progression in early Alzheimer's, and many treat behavioral symptoms. Setting expectations matters.",
        body: [
            { type: "h2", text: "Cognitive medications (symptom management)" },
            { type: "ul", items: [
                "Donepezil (Aricept) — cholinesterase inhibitor. Mild improvement in memory + attention. Common in mild-to-moderate stages.",
                "Rivastigmine (Exelon) — same class, available as a patch (easier if pills are refused).",
                "Galantamine (Razadyne) — same class.",
                "Memantine (Namenda) — different mechanism. Often added in moderate stage.",
                "Common side effects: nausea, diarrhea, leg cramps, vivid dreams.",
            ]},
            { type: "h2", text: "Disease-modifying treatments (early Alzheimer's only)" },
            { type: "ul", items: [
                "Lecanemab (Leqembi) — IV every 2 weeks. Slows decline ~27% over 18 months in mild-stage patients.",
                "Donanemab (Kisunla) — IV every 4 weeks. Slows decline ~35% in mild-stage.",
                "Both require: mild stage, biomarker confirmation (PET scan or CSF), no APOE4 homozygosity (or careful monitoring).",
                "Both have risk of ARIA (brain swelling or microbleeds) — requires monthly MRI monitoring first 6 months.",
                "Insurance: Medicare covers with strict criteria. Memory clinic referral needed.",
            ]},
            { type: "h2", text: "Behavioral medications" },
            { type: "ul", items: [
                "Brexpiprazole (Rexulti) — first FDA-approved for Alzheimer's agitation, 2023.",
                "Pimavanserin (Nuplazid) — for psychosis in Parkinson's dementia and Lewy body.",
                "Sertraline, citalopram — for depression + some anxiety.",
                "Trazodone — for sleep + agitation, fewer risks than benzos.",
                "Risperidone, quetiapine, olanzapine — older antipsychotics, used cautiously. Increased mortality risk in elderly.",
            ]},
            { type: "callout", tone: "coral", text: "Medications that worsen confusion: benzodiazepines (Ativan, Xanax, Valium), sleep aids (Ambien, Lunesta), anticholinergics (Benadryl, oxybutynin, amitriptyline). Ask your doctor to deprescribe these wherever possible." },
        ],
        faq: [
            { q: "When should we stop dementia medications?", a: "In late stage, most have minimal benefit. Talk to the doctor about deprescribing. Stopping often makes no noticeable difference." },
            { q: "Is Leqembi worth it?", a: "Depends. Modest benefit, real risks, frequent infusions and scans. Discuss with a memory clinic specialist — not every primary care doctor is up to date." },
        ],
        related: ["alzheimers-vs-dementia", "stages-of-dementia", "brexpiprazole-rexulti", "resistance-to-medication"],
        lane_prompt: "What dementia medications are available in 2026?",
        updated_at: "2026-02-27",
    },
    {
        slug: "caregiver-anger",
        group: "learn",
        title: "Caregiver Anger — What to Do With Rage You Didn't Expect",
        meta: "Caregiver rage shocks the people experiencing it. Why it shows up, what it means, and how to manage it.",
        h1: "Caregiver anger",
        summary: "If you've shouted, slammed doors, or fantasized about leaving — you're a normal caregiver, not a bad one. Rage is exhaustion + grief + powerlessness. Naming it is the first step.",
        body: [
            { type: "h2", text: "What it looks like" },
            { type: "ul", items: [
                "Shouting at someone who can't help being confused.",
                "Slamming doors or throwing things.",
                "Snapping at strangers — checkout clerk, your kids, your spouse.",
                "Fantasizing about leaving or about their death.",
                "Intrusive thoughts you'd never say aloud.",
            ]},
            { type: "h2", text: "Why it happens" },
            { type: "p", text: "The brain treats chronic powerlessness like a threat. When you've been told no, ignored, or attacked all day by someone you love — anger is a normal protective response. It doesn't mean you've lost your love. It means you've lost your reserves." },
            { type: "h2", text: "What helps in the moment" },
            { type: "ul", items: [
                "Leave the room. Lock the bathroom door. Cold water on your face.",
                "Box breathing — 4 seconds in, 4 hold, 4 out, 4 hold. Repeat 4 times.",
                "Call ONE person to vent. Doesn't have to be advice — just witnessed.",
                "Step outside. Sky and trees lower cortisol in minutes.",
                "Move. Push a wall, do jumping jacks, walk fast. Discharge the energy.",
            ]},
            { type: "h2", text: "What helps long-term" },
            { type: "ul", items: [
                "Respite. Two hours a week minimum. More if possible.",
                "Therapy — find one who specializes in caregiver trauma or anger management.",
                "Sleep — under-slept caregivers are 4x more likely to have anger outbursts.",
                "Antidepressants if depression underlies the rage. Many caregivers don't realize they're depressed.",
                "Caregiver group — hearing 'me too' is dramatically healing.",
            ]},
            { type: "callout", tone: "coral", text: "If you've physically struck or restrained your loved one — even once — get help today. Call the National Domestic Violence Hotline (1-800-799-7233) or Alzheimer's Association (1-800-272-3900). This is treatable; you are not the first caregiver to do this." },
        ],
        faq: [
            { q: "Does anger mean I shouldn't be the caregiver?", a: "No. It means you need more support. Most caregivers who get respite, therapy, and sleep recover their patience." },
            { q: "Is medication for anger appropriate?", a: "Sometimes. SSRIs (sertraline, escitalopram) help underlying depression and reduce anger flashes. Discuss with your own doctor." },
        ],
        related: ["caregiver-burnout-signs", "caregiver-guilt", "caregiver-isolation", "aggressive-behavior-de-escalation"],
        lane_prompt: "I lost my temper today. How do I deal with caregiver rage?",
        updated_at: "2026-02-27",
    },
    {
        slug: "dementia-and-vision-changes",
        group: "learn",
        title: "Dementia and Vision — What Changes and What To Do",
        meta: "Dementia affects how the brain processes what the eyes see. Misperceptions, hallucinations, and depth-perception problems are common.",
        h1: "Dementia and vision",
        summary: "Vision changes in dementia are often the brain, not the eyes. Knowing what's happening helps you make the world less frightening.",
        body: [
            { type: "h2", text: "Common visual changes" },
            { type: "ul", items: [
                "Depth perception loss — dark floors look like holes; bright water reflections look like obstacles.",
                "Contrast sensitivity drops — they can't distinguish similar tones (white toilet on white floor).",
                "Peripheral vision narrows — they walk into things on the side.",
                "Misperceptions — coats on chairs become people, patterned floors become movement.",
                "Hallucinations — seeing people or animals that aren't there (especially in Lewy body and late Alzheimer's).",
            ]},
            { type: "h2", text: "Adjust the environment" },
            { type: "ul", items: [
                "High-contrast plates and tableware — red plate, white food.",
                "Toilet seat in a contrasting color.",
                "Solid-color floors. Remove patterned rugs.",
                "Bright, even lighting — older eyes need 2-3x more light than yours.",
                "Cover mirrors if they trigger fear or confusion.",
                "Remove or cover dark mats — they look like holes.",
                "Place black mat in front of exit doors — discourages exit by 'looking like a hole.'",
            ]},
            { type: "h2", text: "Routine eye care" },
            { type: "ul", items: [
                "Annual eye exam — diabetes, glaucoma, macular degeneration are still common.",
                "Cataract surgery is still appropriate in early-to-middle dementia — improved vision often improves cognition.",
                "Up-to-date glasses prescription.",
                "Hearing aids — yes, hearing aids. Hearing loss compounds visual confusion.",
            ]},
            { type: "callout", tone: "teal", text: "If your loved one suddenly stops at thresholds, won't step onto a tile floor, or refuses certain rooms — it's likely visual. They're not being stubborn; their brain is showing them something dangerous." },
        ],
        faq: [
            { q: "Should we cover the mirrors?", a: "If they're scared by their reflection or talk to it endlessly, yes. Otherwise, leave them. Many dementia patients enjoy seeing themselves." },
            { q: "Are hallucinations always a problem?", a: "If they're not scary or distressing, often no treatment needed. If they're frightening or driving behavior, talk to the doctor about pimavanserin or low-dose antipsychotics." },
        ],
        related: ["hallucinations", "lewy-body-dementia", "falls-in-dementia", "wandering-prevention-checklist"],
        lane_prompt: "Dad keeps walking into furniture. Is it his eyes or his brain?",
        updated_at: "2026-02-27",
    },

    // ===== BEHAVIOR — three more high-value =====
    {
        slug: "dementia-and-anger",
        group: "behavior",
        title: "Anger in Dementia — Why It Spikes and How to Soften It",
        meta: "Sudden anger and irritability are common in dementia. What drives them and what calms them.",
        h1: "Anger in dementia",
        summary: "Anger in dementia is usually a signal — not an attack. Decoding the signal turns down the volume.",
        body: [
            { type: "h2", text: "What anger is signaling" },
            { type: "ul", items: [
                "Pain (most common, most missed).",
                "Confusion + frustration with their own decline.",
                "Loss of control — being told what to do.",
                "Overstimulation — noise, lights, crowds.",
                "Hunger, thirst, full bladder, fatigue.",
                "Medication side effect.",
                "UTI or other infection.",
            ]},
            { type: "h2", text: "What softens anger" },
            { type: "ul", items: [
                "Validation: 'You're right to be upset.'",
                "Lower your tone. Slow your pace.",
                "Offer control: 'Would you like to do this now or after lunch?'",
                "Reduce input: TV off, fewer people in the room.",
                "Address basic needs: water, snack, bathroom.",
                "Distract with something they love — favorite music, photo album, pet.",
            ]},
            { type: "h2", text: "What worsens anger" },
            { type: "ul", items: [
                "Arguing or correcting.",
                "Multi-step instructions.",
                "Asking too many questions ('did you eat? are you tired? do you need the bathroom?').",
                "Standing too close.",
                "Trying to win.",
            ]},
            { type: "h2", text: "When to consider medication" },
            { type: "ul", items: [
                "Daily anger episodes that don't respond to environmental changes.",
                "Anger that causes physical risk to caregiver or loved one.",
                "Anger linked to depression — treat the depression first.",
                "First-line: brexpiprazole (Rexulti, FDA-approved for Alzheimer's agitation).",
                "Avoid benzos and anticholinergics — they worsen the underlying problem.",
            ]},
        ],
        faq: [
            { q: "Is the anger permanent?", a: "Often middle-stage peak, decreasing in late stage. Identifying and treating triggers helps in any stage." },
            { q: "Should we try CBD?", a: "Some families report benefit. Evidence is thin but harm is low. Use isolated-CBD products (no THC) and discuss with the doctor." },
        ],
        related: ["aggression", "aggressive-behavior-de-escalation", "agitation", "dementia-and-pain"],
        lane_prompt: "Mom is so angry all the time now. What's happening?",
        updated_at: "2026-02-27",
    },
    {
        slug: "middle-of-the-night-confusion",
        group: "behavior",
        title: "Middle-of-the-Night Confusion — The 2am Wake-Up",
        meta: "Why dementia patients wake up confused at 2am and what to do without losing your own sleep.",
        h1: "Middle of the night confusion",
        summary: "The 2am wake-up — disoriented, dressed for work, asking for parents long-dead — is one of the hardest caregiving moments. Here's the plan.",
        body: [
            { type: "h2", text: "Why it happens" },
            { type: "ul", items: [
                "Confusion peaks at sleep transitions — partial waking with full disorientation.",
                "Bladder pressure wakes them, then confusion takes over.",
                "Sundowning extends past midnight in some patients.",
                "Sleep apnea — undertreated and common.",
                "Pain, especially arthritis when shifting position.",
                "Medications wearing off (or kicking in).",
            ]},
            { type: "h2", text: "In the moment" },
            { type: "ol", items: [
                "Speak softly. Don't turn on bright lights — use a small lamp.",
                "Name the time and place gently: 'It's 2 in the morning. We're at home.' (Don't argue.)",
                "Address physical needs — bathroom, water, warmth.",
                "Walk them back to bed. Stay until they settle.",
                "Don't bring up tomorrow's plans — they'll start the day in their head.",
            ]},
            { type: "h2", text: "Prevent the next one" },
            { type: "ul", items: [
                "Last bathroom visit right before sleep.",
                "Limit fluids 2 hours before bed.",
                "Heavier dinner (carbs + protein) to maintain blood sugar overnight.",
                "Warm room. Heated mattress pad if helpful.",
                "Comfort object — stuffed animal, weighted blanket, familiar pillow.",
                "Melatonin (3-5mg) tried first before any sleep prescription.",
                "Sleep apnea evaluation if snoring is loud.",
            ]},
            { type: "callout", tone: "teal", text: "Many caregivers eventually move to separate bedrooms — it's the single best move for protecting their own sleep. A baby monitor + door alarm covers the safety needs without you waking with every shift." },
        ],
        faq: [
            { q: "Should we try benzos for sleep?", a: "Almost never. They worsen confusion, increase fall risk, and become harder to stop the longer they're used. Trazodone, melatonin, mirtazapine are safer first tries." },
            { q: "What about sleeping pills like Ambien?", a: "Same caution as benzos. Linked to falls, complex sleep behaviors (wandering, eating in sleep), and worse outcomes. Reserve for last resort." },
        ],
        related: ["sundowning", "sleep-disturbance", "day-night-reversal", "dementia-and-pain"],
        lane_prompt: "Dad keeps waking up at 2am confused. What do we do?",
        updated_at: "2026-02-27",
    },
];
